import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import {
  GALLERY_PAGE,
  MEDIA_TABS,
  PORTRAITS,
  MUSIC_VIDEOS,
  COVERS,
  LIVE_VIDEOS,
  PODCASTS,
  ytThumb,
  SEO_META,
} from '../mock';
import Reveal from '../components/Reveal';
import Seo from '../components/Seo';
import YoutubeDialog from '../components/YoutubeDialog';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';

const slug = (t) => t.toLowerCase().replace(/\s+/g, '-');

// Curated mix for the ALL tab — a taste of every category
const ALL_ITEMS = [
  { ...PORTRAITS[0], kind: 'photo' },
  { ...MUSIC_VIDEOS[0], kind: 'video' },
  { ...PODCASTS[0], kind: 'video' },
  { ...COVERS[0], kind: 'video' },
  { ...PORTRAITS[1], kind: 'photo' },
  { ...MUSIC_VIDEOS[1], kind: 'video' },
  { ...COVERS[1], kind: 'video' },
  { ...PODCASTS[2], kind: 'video' },
];

const VideoCard = ({ item, onPlay, delay }) => (
  <Reveal delay={delay}>
    <button
      onClick={() => onPlay(item)}
      data-testid={`video-card-${item.id}`}
      aria-label={`Play ${item.title}`}
      className="group relative block w-full overflow-hidden text-left"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={ytThumb(item.youtubeId)}
          alt={item.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent opacity-70 transition-opacity duration-500 group-hover:opacity-100" />
        <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-ink/50 text-white backdrop-blur-sm transition-colors duration-300 group-hover:border-gold group-hover:text-gold">
          <Play size={18} fill="currentColor" className="ml-0.5" />
        </span>
        <div className="absolute inset-x-0 bottom-0 p-5">
          <p className="text-[9px] tracking-[0.35em] text-gold">{item.show}</p>
          <p className="mt-1 font-serif text-lg leading-snug tracking-[0.04em] text-white">
            {item.title}
          </p>
        </div>
      </div>
    </button>
  </Reveal>
);

const PhotoCard = ({ item, onOpen, delay }) => (
  <Reveal delay={delay}>
    <button
      onClick={() => onOpen(item)}
      data-testid={`portrait-card-${item.id}`}
      aria-label={`View ${item.title}`}
      className="group relative block w-full overflow-hidden text-left"
    >
      {/* Natural aspect ratio — the full photograph always remains visible */}
      <img
        src={item.image}
        alt={item.title}
        loading="lazy"
        className="h-auto w-full transition-transform duration-700 ease-out group-hover:scale-[1.02]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
      <div className="absolute inset-x-0 bottom-0 p-5 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
        <p className="text-[9px] tracking-[0.35em] text-gold">PORTRAITS</p>
        <p className="mt-1 font-serif text-lg tracking-[0.05em] text-white">{item.title}</p>
      </div>
    </button>
  </Reveal>
);

const asKind = (list, kind) => list.map((item) => ({ ...item, kind }));

const TAB_ITEMS = {
  Portraits: asKind(PORTRAITS, 'photo'),
  'Music Videos': asKind(MUSIC_VIDEOS, 'video'),
  Covers: asKind(COVERS, 'video'),
  'Live Performances': asKind(LIVE_VIDEOS, 'video'),
  Podcasts: asKind(PODCASTS, 'video'),
};

const MasonryGrid = ({ items, onOpen }) => (
  <div className="mx-auto max-w-[1400px] columns-1 gap-5 sm:columns-2 lg:columns-3">
    {items.map((item, i) => {
      const Card = item.kind === 'photo' ? PhotoCard : VideoCard;
      return (
        <div key={item.id} className="mb-5 break-inside-avoid">
          <Card item={item} onOpen={onOpen} onPlay={onOpen} delay={(i % 6) * 80} />
        </div>
      );
    })}
  </div>
);

const EmptyCategory = () => (
  <div className="mx-auto max-w-[1400px] border border-white/10 px-6 py-20 text-center">
    <p className="text-[10px] uppercase tracking-[0.4em] text-gold">Coming Soon</p>
    <p className="mt-4 font-serif text-2xl font-light tracking-[0.04em] text-neutral-300">
      New live performance videos are on their way.
    </p>
  </div>
);

const VideoGrid = ({ items, onOpen }) => (
  <div className="mx-auto grid max-w-[1400px] gap-6 sm:grid-cols-2 lg:grid-cols-3">
    {items.map((item, i) => (
      <VideoCard key={item.id} item={item} onPlay={onOpen} delay={(i % 3) * 90} />
    ))}
  </div>
);

const GalleryContent = ({ tab, items, onOpen }) => {
  if (tab === 'All' || tab === 'Portraits') return <MasonryGrid items={items} onOpen={onOpen} />;
  if (items.length === 0) return <EmptyCategory />;
  return <VideoGrid items={items} onOpen={onOpen} />;
};

const GalleryPage = () => {
  const { hero } = GALLERY_PAGE;
  const [tab, setTab] = useState('All');
  const [lightbox, setLightbox] = useState(null);
  const [video, setVideo] = useState(null);

  const items = TAB_ITEMS[tab] || ALL_ITEMS;

  const photos = items.filter((i) => i.kind === 'photo');
  const active = lightbox !== null ? photos[lightbox] : null;
  const step = (dir) =>
    setLightbox((cur) => (cur === null ? cur : (cur + dir + photos.length) % photos.length));

  const openItem = (item) => {
    if (item.kind === 'photo') setLightbox(photos.indexOf(item));
    else setVideo(item);
  };

  return (
    <main className="bg-ink">
      <Seo {...SEO_META.gallery} />
      {/* Hero */}
      <section className="bg-ink px-6 pb-6 pt-32 md:px-10 md:pt-36">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <p className="mb-5 flex items-center gap-4 text-[10px] uppercase tracking-[0.5em] text-gold">
              {hero.label}
              <span className="h-px w-14 bg-gold/50" />
            </p>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-[0.02em] text-white md:text-6xl">
              {hero.title}
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-neutral-400">{hero.text}</p>
          </Reveal>
        </div>
      </section>

      {/* Filter tabs */}
      <section className="sticky top-20 z-30 bg-ink/90 py-5 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-7 gap-y-3 px-6 md:px-10">
          {MEDIA_TABS.map((cat) => (
            <button
              key={cat}
              data-testid={`media-tab-${slug(cat)}`}
              onClick={() => {
                setTab(cat);
                setLightbox(null);
              }}
              className={`text-[10px] tracking-[0.25em] transition-colors duration-300 hover:text-gold ${
                tab === cat ? 'text-gold' : 'text-neutral-400'
              }`}
            >
              {cat.toUpperCase()}
              {tab === cat && <span className="mt-1.5 block h-px w-full bg-gold" />}
            </button>
          ))}
        </div>
      </section>

      {/* Content */}
      <section className="px-6 pb-20 pt-6 md:px-10">
        <GalleryContent tab={tab} items={items} onOpen={openItem} />
      </section>

      {/* Photo lightbox — full image, never cropped */}
      <Dialog open={lightbox !== null} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="border-white/10 bg-ink p-0 text-neutral-200 sm:max-w-3xl">
          <DialogTitle className="sr-only">{active?.title || 'Gallery'}</DialogTitle>
          <DialogDescription className="sr-only">Gallery lightbox</DialogDescription>
          {active && (
            <div>
              <div className="relative flex items-center justify-center overflow-hidden bg-black">
                <img
                  src={active.image}
                  alt={active.title}
                  className="max-h-[75vh] w-full object-contain"
                />
                <button
                  onClick={() => step(-1)}
                  aria-label="Previous image"
                  className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-ink/50 text-white backdrop-blur-sm transition-colors duration-300 hover:border-gold hover:text-gold"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => step(1)}
                  aria-label="Next image"
                  className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-ink/50 text-white backdrop-blur-sm transition-colors duration-300 hover:border-gold hover:text-gold"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
              <div className="flex items-center justify-between px-6 py-5">
                <div>
                  <p className="text-[9px] tracking-[0.35em] text-gold">PORTRAITS</p>
                  <p className="mt-1 font-serif text-xl tracking-[0.06em] text-white">
                    {active.title}
                  </p>
                </div>
                <p className="text-[10px] tracking-[0.25em] text-neutral-500">
                  {String(lightbox + 1).padStart(2, '0')} / {String(photos.length).padStart(2, '0')}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Video / podcast player */}
      <YoutubeDialog video={video} onClose={() => setVideo(null)} />
    </main>
  );
};

export default GalleryPage;
